import React, {useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {useStores} from '../../../stores/Context';
import styles from './ManualIndicatorEntry.module.scss';
import {formatRecordedAt} from './indicatorHistoryContext';

const today = () => new Date().toISOString().slice(0, 10);
const normalizeValue = (value) => String(value).trim().replace(',', '.');
const formatNumber = (value) => Number(value).toLocaleString('fr-FR', {maximumFractionDigits: 4});
const formatDate = (date) => new Intl.DateTimeFormat('fr-FR', {day: '2-digit', month: 'short', year: 'numeric'}).format(new Date(`${date}T12:00:00`));

const ManualIndicatorEntry = ({idWidget, unit, onPersisted, compact = false}) => {
    const {widgetStore} = useStores();
    const [observedOn, setObservedOn] = useState(today());
    const [entries, setEntries] = useState([]);
    const [causeCatalog, setCauseCatalog] = useState([]);
    const [selectedCauses, setSelectedCauses] = useState([]);
    const [newCause, setNewCause] = useState('');
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const headers = useMemo(() => ({Authorization: `Bearer ${widgetStore._token}`}), [widgetStore._token]);

    const load = async () => {
        if (!idWidget || !widgetStore._token) return;
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/indicateurs/${idWidget}/manual-entries`,
                {headers, params: {from: '2000-01-01', to: today()}},
            );
            setEntries(response.data?.data?.entries || []);
            setCauseCatalog(response.data?.data?.cause_catalog || []);
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Les relevés manuels sont indisponibles.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [idWidget, widgetStore._token]);

    useEffect(() => {
        setValue('');
        setSelectedCauses([]);
        setNewCause('');
        setNotice('');
    }, [observedOn]);

    const submit = async (event) => {
        event.preventDefault();
        const normalized = normalizeValue(value);
        if (!normalized || !Number.isFinite(Number(normalized))) {
            setError('Saisissez une valeur numérique avant de l’enregistrer.');
            return;
        }

        setSaving(true);
        setError('');
        setNotice('');
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/indicateurs/${idWidget}/manual-entries`,
                {
                    observed_on: observedOn,
                    value: normalized,
                    causes: selectedCauses.map(cause => cause.id ? {id: cause.id} : {label: cause.label}),
                },
                {headers},
            );
            const entry = response.data?.data?.entry;
            setEntries(previous => {
                return [entry, ...previous]
                    .filter(Boolean)
                    .sort((a, b) => `${b.date}-${b.recorded_at || ''}`.localeCompare(`${a.date}-${a.recorded_at || ''}`));
            });
            setValue('');
            setSelectedCauses([]);
            setCauseCatalog(previous => {
                const next = [...previous];
                (entry?.causes || []).forEach(cause => {
                    if (!next.some(item => String(item.id) === String(cause.id))) next.push(cause);
                });
                return next.sort((a, b) => a.label.localeCompare(b.label, 'fr'));
            });
            setNotice(response.data?.message || 'Saisie ajoutée à l’historique.');
            onPersisted?.(entry);
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Le relevé n’a pas été enregistré. Réessayez.');
        } finally {
            setSaving(false);
        }
    };

    const recentEntries = [...entries]
        .sort((a, b) => `${b.date}-${b.recorded_at || ''}`.localeCompare(`${a.date}-${a.recorded_at || ''}`))
        .slice(0, 5);
    const todayValue = recentEntries.find(entry => entry.date === today())?.value;
    const toggleCause = (cause) => {
        setSelectedCauses(previous => previous.some(item => String(item.id) === String(cause.id))
            ? previous.filter(item => String(item.id) !== String(cause.id))
            : [...previous, cause]);
    };
    const addNewCause = () => {
        const label = newCause.trim();
        if (!label) return;
        if (selectedCauses.some(cause => cause.label.localeCompare(label, 'fr', {sensitivity: 'accent'}) === 0)) {
            setNewCause('');
            return;
        }
        setSelectedCauses(previous => [...previous, {label}]);
        setNewCause('');
    };

    return <section className={styles.panel} aria-label="Saisie manuelle de l’indicateur">
        {!compact && <header className={styles.header}>
            <div>
                <span className={styles.eyebrow}>RELEVÉ TERRAIN</span>
                <h3>Nouvelle saisie</h3>
                <p>Chaque observation est horodatée et apparaît séparément dans l’historique, l’analyse et le Pareto des non-performances.</p>
            </div>
            {todayValue !== undefined && <div className={styles.todayValue}><span>Aujourd’hui</span><strong>{formatNumber(todayValue)} {unit || ''}</strong></div>}
        </header>}

        <form className={styles.form} onSubmit={submit}>
            <label>
                Date du relevé
                <input type="date" value={observedOn} max={today()} onChange={event => setObservedOn(event.target.value)} required/>
            </label>
            <label className={styles.valueField}>
                Valeur{unit ? ` (${unit})` : ''}
                <input
                    type="text"
                    inputMode="decimal"
                    value={value}
                    onChange={event => setValue(event.target.value)}
                    placeholder="Ex. 15,5"
                    maxLength="20"
                    required
                />
            </label>
            <button type="submit" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer le relevé'}</button>

            <div className={styles.causesField}>
                <div className={styles.causesHeading}>
                    <span>Causes de non-performance associées <em>facultatif</em></span>
                    <small>Ces causes alimentent le Pareto de cet indicateur.</small>
                </div>
                {causeCatalog.length > 0 && <div className={styles.causeChoices}>
                    {causeCatalog.map(cause => {
                        const selected = selectedCauses.some(item => String(item.id) === String(cause.id));
                        return <button
                            key={cause.id}
                            type="button"
                            className={selected ? styles.causeSelected : ''}
                            aria-pressed={selected}
                            onClick={() => toggleCause(cause)}
                        >{selected ? '✓ ' : '+ '}{cause.label}</button>;
                    })}
                </div>}
                <div className={styles.newCause}>
                    <input
                        type="text"
                        value={newCause}
                        maxLength="160"
                        placeholder="Ajouter une autre cause (ex. Absence opérateur)"
                        onChange={event => setNewCause(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                addNewCause();
                            }
                        }}
                    />
                    <button type="button" onClick={addNewCause} disabled={!newCause.trim()}>Ajouter</button>
                </div>
                {selectedCauses.length > 0 && <div className={styles.selectedCauses} aria-label="Causes sélectionnées">
                    {selectedCauses.map((cause, index) => <button
                        key={cause.id || `new-${cause.label}-${index}`}
                        type="button"
                        onClick={() => setSelectedCauses(previous => previous.filter((item, itemIndex) => itemIndex !== index))}
                        title="Retirer cette cause"
                    >{cause.label} <span>×</span></button>)}
                </div>}
            </div>
        </form>

        {notice && <p className={styles.notice} role="status">✓ {notice}</p>}
        {error && <p className={styles.error} role="alert">{error}</p>}

        <div className={styles.history}>
            <div className={styles.historyHeader}><strong>Derniers relevés</strong><span>{loading ? 'Chargement…' : `${entries.length} relevé${entries.length > 1 ? 's' : ''}`}</span></div>
            {!loading && recentEntries.length === 0 && <p className={styles.empty}>Aucun relevé enregistré. Commencez par une première mesure.</p>}
            {!loading && recentEntries.map(entry => <div className={styles.historyRow} key={`${entry.date}-${entry.id}`}>
                <span>
                    {formatDate(entry.date)}
                    <small>Saisi le {formatRecordedAt(entry.recorded_at)}</small>
                    {entry.causes?.length ? <small>{entry.causes.map(cause => cause.label).join(' · ')}</small> : <small>Aucune cause associée</small>}
                </span><strong>{formatNumber(entry.value)} {unit || ''}</strong>
            </div>)}
        </div>
    </section>;
};

export default ManualIndicatorEntry;
